"use client";
import { useEffect, useRef } from "react";
import {
  ScrollText,
  Users,
  Server,
  ShieldCheck,
  Lock,
  Archive,
} from "lucide-react";

const TRUST = [
  {
    icon: ScrollText,
    title: "Audit logging",
    body: "Every action recorded: user, timestamp, matched datasets. Immutable.",
  },
  {
    icon: Users,
    title: "Role-based access",
    body: "Viewer · Analyst · Admin · Owner. Org-isolated by default.",
  },
  {
    icon: Server,
    title: "Data isolation",
    body: "Tenant-scoped at the architecture level. Hosted in India.",
  },
];

const COMPANY_STATS = [
  { target: 8, suffix: "+", label: "Years of experience" },
  { target: 200, suffix: "+", label: "Clients served" },
  { target: 88, suffix: "%", label: "Client retention" },
  { target: 6, suffix: "+", label: "Industries served" },
];

const PRODUCT_STATS = [
  { target: 300, suffix: "K+", label: "Entities indexed" },
  { target: 225, suffix: "K+", label: "Debarred directors indexed" },
  { target: 150, suffix: "×", label: "Faster graph BFS" },
  { target: 77, suffix: "×", label: "Faster cross-source name match" },
];

function CountUp({ target, suffix }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const DURATION = 600;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let rafId = null;

    const reset = () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.textContent = "0" + suffix;
    };
    const run = () => {
      if (rafId) cancelAnimationFrame(rafId);
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / DURATION, 1);
        const value = Math.round(target * easeOutCubic(t));
        el.textContent = value + suffix;
        if (t < 1) rafId = requestAnimationFrame(tick);
        else el.textContent = target + suffix;
      };
      rafId = requestAnimationFrame(tick);
    };

    if (!("IntersectionObserver" in window)) {
      run();
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) run();
          else reset();
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      io.disconnect();
    };
  }, [target, suffix]);

  return <span ref={ref}>0{suffix}</span>;
}

export default function TrustSection() {
  const trustRef = useRef(null);

  useEffect(() => {
    const root = trustRef.current;
    if (!root) return;
    const cards = root.querySelectorAll(".border-glow-card");
    if (!cards.length) return;

    const update = (card, e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const dx = x - cx;
      const dy = y - cy;
      let kx = Infinity;
      let ky = Infinity;
      if (dx !== 0) kx = cx / Math.abs(dx);
      if (dy !== 0) ky = cy / Math.abs(dy);
      const edge = Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
      let angle = 0;
      if (dx !== 0 || dy !== 0) {
        let deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
        if (deg < 0) deg += 360;
        angle = deg;
      }
      card.style.setProperty("--edge-proximity", (edge * 100).toFixed(3));
      card.style.setProperty("--cursor-angle", angle.toFixed(3) + "deg");
    };

    const handlers = [];
    cards.forEach((card) => {
      const onMove = (e) => update(card, e);
      card.addEventListener("pointermove", onMove, { passive: true });
      handlers.push([card, onMove]);
    });
    return () => {
      handlers.forEach(([card, onMove]) =>
        card.removeEventListener("pointermove", onMove)
      );
    };
  }, []);

  return (
    <>
      {/* TRUST BAND */}
      <section ref={trustRef} className="section-pad bg-navy relative overflow-hidden text-white">
        <div className="pointer-events-none absolute inset-0 grid-bg opacity-50" aria-hidden="true"></div>
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(30,91,255,0.18) 0%, transparent 60%)" }}
          aria-hidden="true"
        ></div>

        <div className="container-c relative">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue">Built for audit</p>
            <h2 className="mt-3 font-display text-[32px] leading-[1.08] tracking-tight text-balance sm:text-[40px] md:text-[48px]">
              The trail your regulator will accept.
            </h2>
          </div>

          <div className="mt-14 grid gap-5 md:mt-16 md:grid-cols-3">
            {TRUST.map(({ icon: Icon, title, body }) => (
              <div key={title} className="border-glow-card p-7">
                <span className="edge-light" aria-hidden="true"></span>
                <div className="border-glow-inner">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue/20 text-blue">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display text-[19px] font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-white/70">{body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-medium text-white/80">
              <ShieldCheck className="h-4 w-4 text-blue" /> SOC 2 aligned
            </span>
            <span className="text-white/30">·</span>
            <span className="inline-flex items-center gap-2 text-[13px] font-medium text-white/80">
              <Lock className="h-4 w-4 text-blue" /> Encryption in transit + at rest
            </span>
            <span className="text-white/30">·</span>
            <span className="inline-flex items-center gap-2 text-[13px] font-medium text-white/80">
              <Archive className="h-4 w-4 text-blue" /> 90-day audit retention
            </span>
          </div>
        </div>
      </section>

      {/* FROM THE TEAM BEHIND CLOUDASTRA */}
      <section className="section-pad bg-white">
        <div className="container-c">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue">From the team behind Cloudastra</p>
            <h2 className="mt-3 font-display text-[32px] leading-[1.08] tracking-tight text-balance text-ink sm:text-[40px] md:text-[44px]">
              A software company India&apos;s enterprises trust.
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-muted text-pretty">
              Cloudastra Technologies builds digital, cloud, and AI-agency solutions for media, fintech, and cybersecurity leaders. RiskIntel is one of the products born out of that work.
            </p>
          </div>

          <div className="mt-14">
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">Cloudastra in numbers</p>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
              {COMPANY_STATS.map(({ target, suffix, label }) => (
                <div key={label} className="rounded-2xl border border-line bg-surface p-6 text-center">
                  <p className="font-display text-[36px] font-bold tracking-tightest text-blue md:text-[44px]">
                    <CountUp target={target} suffix={suffix} />
                  </p>
                  <p className="mt-1 text-[13px] font-medium text-ink">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-purple">RiskIntel in numbers</p>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
              {PRODUCT_STATS.map(({ target, suffix, label }) => (
                <div key={label} className="rounded-2xl border border-purple/30 bg-purple-soft p-6 text-center">
                  <p className="font-display text-[36px] font-bold tracking-tightest text-purple md:text-[44px]">
                    <CountUp target={target} suffix={suffix} />
                  </p>
                  <p className="mt-1 text-[13px] font-medium text-ink">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
